// MIRRORS `OmpMomentActionDefs` / `OmpMomentActionsByMoment` in the admin. The Home moment card's actions
// per moment, their default labels and icons, and the read-side conversion of a Home `next_order_details`.
import { LegacyControlKey, Moment, MOMENT_SLUGS, MomentActionKey, SectionKey } from '@/constants'
import type { MomentCardAction, MomentCardData, MomentKey, PortalSection } from '@/types'
import { resolveCompareAtPrice } from './omp'

export const MOMENT_ACTION_DEFAULTS: Record<MomentActionKey, { label: string; icon: string }> = {
  [MomentActionKey.SendNow]: { label: 'Send Now', icon: 'fa-solid fa-paper-plane' },
  [MomentActionKey.Reschedule]: { label: 'Reschedule', icon: 'fa-regular fa-calendar-days' },
  [MomentActionKey.Skip]: { label: 'Skip', icon: 'fa-solid fa-forward' },
  [MomentActionKey.Swap]: { label: 'Swap', icon: 'fa-solid fa-arrow-right-arrow-left' },
  [MomentActionKey.BuyAgain]: { label: 'Buy Again', icon: 'fa-solid fa-cart-shopping' },
  [MomentActionKey.TrackOrder]: { label: 'Track Order', icon: 'fa-solid fa-truck-fast' },
  [MomentActionKey.RebillItem]: { label: 'Update Payment', icon: 'fa-regular fa-credit-card' },
  [MomentActionKey.Reactivate]: { label: 'Reactivate & Save', icon: 'fa-solid fa-arrows-rotate' },
}

export const MOMENT_ACTIONS: Partial<Record<MomentKey, MomentActionKey[]>> = {
  [MOMENT_SLUGS[Moment.SubscriptionActive]]: [
    MomentActionKey.SendNow,
    MomentActionKey.Reschedule,
    MomentActionKey.Skip,
    MomentActionKey.Swap,
  ],
  [MOMENT_SLUGS[Moment.OrderPending]]: [MomentActionKey.BuyAgain, MomentActionKey.Swap, MomentActionKey.Reschedule],
  [MOMENT_SLUGS[Moment.OrderShipped]]: [MomentActionKey.TrackOrder, MomentActionKey.BuyAgain],
  [MOMENT_SLUGS[Moment.PaymentIssue]]: [MomentActionKey.RebillItem],
  [MOMENT_SLUGS[Moment.SubscriptionCancelled]]: [MomentActionKey.Reactivate, MomentActionKey.BuyAgain],
}

export const DISCOUNT_MOMENTS: MomentKey[] = [
  MOMENT_SLUGS[Moment.PaymentIssue],
  MOMENT_SLUGS[Moment.SubscriptionCancelled],
]

export const MOMENT_VISIBLE_ACTIONS = 3

export const MOMENT_VISIBLE_ACTIONS_COMPACT = 2

export const MORE_OPTIONS_ICON = 'fa-solid fa-ellipsis'

export function momentActionLabel(action: MomentCardAction): string {
  return action.label || MOMENT_ACTION_DEFAULTS[action.key]?.label || ''
}

const LEGACY_ACTION_FIELDS: Partial<Record<MomentActionKey, string>> = {
  [MomentActionKey.RebillItem]: LegacyControlKey.ResumeSubscription,
}

const RETIRED_DEFAULT_LABELS: Partial<Record<MomentActionKey, string>> = {
  [MomentActionKey.RebillItem]: 'Resume Subscription',
}

function legacyLabel(key: MomentActionKey, label: string | undefined): string {
  return label && label !== RETIRED_DEFAULT_LABELS[key] ? label : MOMENT_ACTION_DEFAULTS[key].label
}

export function momentCardFromNextOrderDetails(
  data: Record<string, any> | undefined,
  moment: MomentKey
): MomentCardData {
  const source = data ?? {}
  return {
    offer_strip_text: source.offer_strip_text,
    actions: (MOMENT_ACTIONS[moment] ?? []).map(key => ({
      key,
      is_visible: source[`show_${LEGACY_ACTION_FIELDS[key] ?? key}`] !== false,
      label: legacyLabel(key, source[`${LEGACY_ACTION_FIELDS[key] ?? key}_label`]),
      icon: MOMENT_ACTION_DEFAULTS[key].icon,
    })),
    show_discount: DISCOUNT_MOMENTS.includes(moment) ? true : undefined,
    border_color: source.border_color,
    display_inside_card: source.display_inside_card,
  }
}

export function withMomentCard(sections: PortalSection[], moment: MomentKey): PortalSection[] {
  if (sections.some(section => section.key === SectionKey.MomentCard)) {
    return sections
  }
  return sections.map(section =>
    section.key === SectionKey.NextOrderDetails
      ? { ...section, key: SectionKey.MomentCard, data: momentCardFromNextOrderDetails(section.data, moment) }
      : section
  )
}

export interface MomentDiscount {
  regular: number
  price: number
  saved: number
  percent: number
}

export function resolveMomentDiscount(item: any): MomentDiscount | undefined {
  const price = Number(item?.current_price ?? item?.price ?? 0)
  const regular = resolveCompareAtPrice(item)
  if (regular == null || !(regular > price)) {
    return undefined
  }
  return {
    regular,
    price,
    saved: regular - price,
    percent: Math.round(((regular - price) / regular) * 100),
  }
}
